/**
 * Video Model Compatibility Registry
 *
 * The model repository is the source of truth for video model metadata,
 * capability flags, reference contracts, endpoints, and prompt enhancement
 * workflow IDs. This module preserves the legacy VideoControls-facing shape
 * while deriving it from repository entries.
 */

import {
  MODEL_REPOSITORY,
  ModelFamily,
  ModelSurface,
  ModelTopology,
  ReferenceTransportStrategy,
  mergeModelContracts,
} from './models';

export const VIDEO_SEQUENCE_SUPPORT = Object.freeze({
  MULTI_PROMPT: 'multi-prompt',
  PROMPT_COLLATION: 'prompt-collation',
});

function getFeatureOptions(feature) {
  return Array.isArray(feature?.options) ? [...feature.options] : null;
}

function getFeatureDefault(feature) {
  return feature?.default ?? null;
}

function mapSequenceSupport(sequenceFeature) {
  if (!sequenceFeature?.enabled) return null;
  if (sequenceFeature.strategy === 'prompt-collation') {
    return VIDEO_SEQUENCE_SUPPORT.PROMPT_COLLATION;
  }
  return VIDEO_SEQUENCE_SUPPORT.MULTI_PROMPT;
}

function mergeVideoFeatures(base = {}, override = {}) {
  const merged = { ...(base || {}), ...(override || {}) };

  for (const key of Object.keys(merged)) {
    if (
      base?.[key]
      && override?.[key]
      && typeof base[key] === 'object'
      && typeof override[key] === 'object'
      && !Array.isArray(base[key])
      && !Array.isArray(override[key])
    ) {
      merged[key] = { ...base[key], ...override[key] };
    }
  }

  return merged;
}

function getRouteContract(model, topology) {
  return topology ? model?.routes?.[topology] || null : null;
}

function getRepositoryFeatureContract(modelOrId, topology = null) {
  const model = resolveVideoRepositoryModel(modelOrId);
  if (!model) return {};
  const route = getRouteContract(model, topology);
  return mergeVideoFeatures(model.features, route?.features);
}

function getRepositoryReferenceContract(modelOrId, topology = null) {
  const model = resolveVideoRepositoryModel(modelOrId);
  if (!model) return {};
  const route = getRouteContract(model, topology);
  return route?.references
    ? mergeModelContracts(model.references, route.references)
    : model.references;
}

function mapLastFrameFeature(lastFrameFeature) {
  if (!lastFrameFeature?.enabled) return null;

  const {
    enabled: _enabled,
    ...lastFrameContract
  } = lastFrameFeature;

  return lastFrameContract;
}

function mapVideoCapabilities(model) {
  const features = model.features || {};
  const aspectRatios = getFeatureOptions(features.aspectRatio);
  const resolutions = getFeatureOptions(features.resolution);
  const durations = getFeatureOptions(features.duration);
  const sequenceSupport = mapSequenceSupport(features.sequence);
  const lastFrame = mapLastFrameFeature(features.lastFrame);
  const nativeElementsEnabled = Boolean(features.nativeElements?.enabled);

  const capabilities = {
    aspectRatios,
    durations,
    durationConstraints: features.durationConstraints || null,
    defaultDuration: getFeatureDefault(features.duration),
    audio: Boolean(features.generateAudio?.enabled),
    audioDefault: typeof features.generateAudio?.default === 'boolean' ? features.generateAudio.default : null,
    cameraMovement: features.cameraMovement?.enabled !== false,
  };

  if (resolutions) {
    capabilities.resolutions = resolutions;
    capabilities.defaultResolution = getFeatureDefault(features.resolution);
  }

  if (features.duration?.suffix) {
    capabilities.durationSuffix = features.duration.suffix;
  }

  if (lastFrame) {
    capabilities.lastFrame = lastFrame;
  }

  if (sequenceSupport) {
    capabilities.sequenceSupport = sequenceSupport;
    capabilities.multiPrompt = sequenceSupport === VIDEO_SEQUENCE_SUPPORT.MULTI_PROMPT;
  }

  if (nativeElementsEnabled) {
    capabilities.nativeElements = true;
    capabilities.maxElements = features.nativeElements?.max || 0;
  }

  return capabilities;
}

function mapCreativeAdapter(model) {
  const features = model.features || {};

  if (mapSequenceSupport(features.sequence) === VIDEO_SEQUENCE_SUPPORT.MULTI_PROMPT) {
    return 'video-sequence-inline';
  }

  return 'video-single-inline';
}

function mapEndpoint(model, topology) {
  return model.routes?.[topology]?.endpoint || null;
}

function mapReferenceLayoutFromContract(references = {}) {
  const strategy = references.transport?.strategy;
  const buckets = references.transport?.buckets;

  if (
    !buckets
    || ![
      ReferenceTransportStrategy.ORDERED_BUCKETS,
      ReferenceTransportStrategy.NATIVE_ELEMENTS,
    ].includes(strategy)
  ) {
    return null;
  }

  return {
    strategy: strategy === ReferenceTransportStrategy.NATIVE_ELEMENTS
      ? 'native-elements'
      : 'ordered-buckets',
    buckets,
    totalMax: references.limits?.total ?? 0,
    ...(references.limits?.nativeElements
      ? { nativeElementsMax: references.limits.nativeElements }
      : {}),
  };
}

function mapReferenceLayout(model) {
  return mapReferenceLayoutFromContract(model.references || {});
}

function mapRepositoryVideoModel(model) {
  const referenceLayout = mapReferenceLayout(model);

  return {
    key: model.id,
    label: model.label,
    enhancementKey: model.prompt?.enhancerId || 'video-ltx',
    iconKey: model.iconKey,
    cost: model.cost,
    creativeAdapter: mapCreativeAdapter(model),
    endpoints: {
      t2v: mapEndpoint(model, ModelTopology.TEXT_TO_VIDEO),
      i2v: mapEndpoint(model, ModelTopology.IMAGE_TO_VIDEO),
      reference: mapEndpoint(model, ModelTopology.VIDEO_TO_VIDEO),
    },
    capabilities: mapVideoCapabilities(model),
    ...(referenceLayout ? { referenceLayout } : {}),
    hostReferenceKinds: model.ui?.generate?.hostReferenceKinds || null,
  };
}

function isVideoGenerateModel(model) {
  return Boolean(
    model
    && model.family === ModelFamily.VIDEO
    && model.surfaces?.includes(ModelSurface.VIDEO_GENERATE)
  );
}

function getRepositoryVideoModel(modelId) {
  return MODEL_REPOSITORY.find((model) => (
    model.id === modelId
    && model.family === ModelFamily.VIDEO
  )) || null;
}

// Accept either repository entries or lightweight active-model records from settings state.
function resolveVideoRepositoryModel(modelOrId) {
  if (typeof modelOrId === 'string') return getRepositoryVideoModel(modelOrId);
  if (isVideoGenerateModel(modelOrId)) return modelOrId;
  if (modelOrId?.id) return getRepositoryVideoModel(modelOrId.id);
  return null;
}

function createVideoModelViewModel(modelOrId) {
  const model = resolveVideoRepositoryModel(modelOrId);
  if (!isVideoGenerateModel(model)) return null;
  return mapRepositoryVideoModel(model);
}

/**
 * Derive video picker models from repository entries.
 * Null builds the full catalog; an explicit empty array represents no active models.
 */
export function getVideoModelViewModels(repositoryModels = null) {
  const sourceModels = Array.isArray(repositoryModels)
    ? repositoryModels
    : MODEL_REPOSITORY.filter(isVideoGenerateModel);

  return sourceModels
    .map(createVideoModelViewModel)
    .filter(Boolean);
}

export const VIDEO_MODELS = getVideoModelViewModels();
const VIDEO_MODEL_BY_KEY = new Map(VIDEO_MODELS.map((model) => [model.key, model]));

/**
 * Look up a video model by key, returning the default generated model when the key is unknown.
 * @param {string} key - Model key (e.g. 'ltx', 'klingV3', 'klingO3', 'veo31')
 * @returns {Object}
 */
export function getVideoModel(key) {
  return VIDEO_MODEL_BY_KEY.get(key) ?? VIDEO_MODEL_BY_KEY.get(DEFAULT_VIDEO_MODEL_KEY);
}

/**
 * Look up a video model by key, declining when the key is not a real video model.
 *
 * The strict sibling of `getVideoModel` above, for callers that must not silently
 * generate with a model other than the one they named. `getVideoModel` never returns
 * falsy, so a `if (!model) throw` guard written against it is dead code and an unknown
 * key bills the default model instead of refusing.
 *
 * Storyboard animate is the second such caller — see the rewind docblock below for the
 * first and the same reasoning. Animate's shot manifest routinely names an *image* model
 * on a still shot, so the unknown-key case there is the common path, not an edge.
 *
 * @param {string} key - Model key (e.g. 'ltx', 'klingV3', 'klingO3', 'veo31')
 * @returns {Object|null} the model when the key is a real video model, else null
 */
export function findVideoModel(key) {
  return VIDEO_MODEL_BY_KEY.get(key) ?? null;
}

/**
 * Resolve a model key read back from a persisted generation, for rewind.
 *
 * Rewind is the one caller that must NOT get a fallback. `getVideoModel` above
 * returns the default model for an unknown key, and the selection effect in
 * VideoControls independently replaces an off-list key with the first entry —
 * so a rewind onto a retired model silently generated with a different model
 * than the clip the user rewound to, with nothing on screen saying so.
 *
 * Membership is tested against the caller's own selectable list rather than
 * `VIDEO_MODEL_BY_KEY`, because a key can exist in the catalog while being
 * absent from the surface being rewound into; both cases must decline.
 *
 * @param {string} persistedKey - `generation_params.model` from the stored item
 * @param {Array<{key: string}>} selectableModels - models offered on this surface
 * @returns {string|null} the key when it is genuinely selectable, else null
 */
export function resolveRewindVideoModelKey(persistedKey, selectableModels) {
  if (typeof persistedKey !== 'string' || persistedKey === '') return null;
  if (!Array.isArray(selectableModels)) return null;
  return selectableModels.some((entry) => entry?.key === persistedKey) ? persistedKey : null;
}

export const DEFAULT_VIDEO_MODEL_KEY = 'minimaxH3Max';

/**
 * The default params for the video module, with model-constrained values derived rather
 * than written as literals.
 *
 * Model-constrained values (duration, resolution, aspect ratio) are derived from the
 * default model's capabilities rather than written as literals, so no mount ever has to
 * correct them. A hardcoded `duration: 5` sat below several models' 6s floor, and the
 * mount-time correction landed as a store write inside the composer mode morph: 459ms
 * with a dropped frame against 320ms with none when the correction was suppressed
 * (docs/plans/MODULE-DEFAULTS-OWNERSHIP-SPEC.md).
 *
 * The `??` fallbacks are for a model that declares no options at all, not a second
 * opinion about the value. Validity is enforced by the invariant test, not by them.
 *
 * Two limits, both real and both stated rather than papered over:
 *
 * 1. This resolves the default model against the STATIC repository, while the composer
 *    picks from the ACTIVE one. If the user deactivates the default model in settings,
 *    the seed still names it, the control auto-selects a different active model, and the
 *    corrections come back. Measured when the default was ltx: with it deactivated a cold
 *    mount emitted ltx/6 -> klingV3/6 -> klingV3/5. That is not a regression - trunk did the same -
 *    but it means "no mount ever has to correct these" holds only while the default model
 *    is active. Resolving activation here would need the runtime repository, which config
 *    must not import; the fix belongs at an orchestration seat that already has both.
 * 2. `DEFAULT_VIDEO_MODEL_KEY` is the one default video model: every surface that falls
 *    back to a model when none is named reads it rather than a literal. Model-specific
 *    literals that remain (the extend paths, the attribute slider's Fast mode) name a
 *    model for a capability, not a default. The agent picks through its own
 *    `defaultFor` metadata, and src/services/agent/videoGenerationOptionHelpers.js still
 *    carries a separate DEFAULT_VIDEO_DURATION_SECONDS.
 *
 * Returns a fresh object per call: each consumer owns its own copy, matching the
 * literal semantics this replaced.
 *
 * @returns {Object} default video generation params
 */
export function getDefaultVideoGenerationParams() {
  const caps = getVideoModel(DEFAULT_VIDEO_MODEL_KEY)?.capabilities || {};
  return {
    prompt: '',
    authoredPrompt: '',
    enhancedPromptModuleId: null,
    duration: caps.defaultDuration ?? 5,
    cameraMovement: 'none',
    aspectRatio: caps.aspectRatios?.[0] ?? '16:9',
    referenceBindings: [],
    model: DEFAULT_VIDEO_MODEL_KEY,
    resolution: caps.defaultResolution ?? '1080p',
    generateAudio: false,
    placeSceneRenderOnCanvas: false,
    requestStyleAssetId: null,
  };
}

export function getVideoModelReferenceLayout(key, topology = null) {
  if (!topology) {
    return getVideoModel(key)?.referenceLayout || null;
  }

  return mapReferenceLayoutFromContract(getRepositoryReferenceContract(key, topology));
}

export function getVideoModelNativeElementLimit(key, topology = null) {
  const features = getRepositoryFeatureContract(key, topology);
  const nativeElements = features.nativeElements;

  if (!nativeElements?.enabled) {
    return 0;
  }

  const references = topology ? getRepositoryReferenceContract(key, topology) : null;

  return nativeElements.max
    ?? references?.limits?.nativeElements
    ?? getVideoModel(key)?.referenceLayout?.nativeElementsMax
    ?? 0;
}

/**
 * Whether a model supports a "last frame" reference image.
 * @param {string} key - Model key
 * @returns {boolean}
 */
export function supportsLastFrame(key) {
  return !!getVideoModel(key)?.capabilities?.lastFrame;
}

export function supportsNativeElements(key, topology = null) {
  return getVideoModelNativeElementLimit(key, topology) > 0;
}

export function getVideoSequenceSupport(modelOrKey) {
  const model = typeof modelOrKey === 'string'
    ? getVideoModel(modelOrKey)
    : modelOrKey;

  if (!model) {
    return null;
  }

  return model?.capabilities?.sequenceSupport
    || (model?.capabilities?.multiPrompt ? VIDEO_SEQUENCE_SUPPORT.MULTI_PROMPT : null);
}

export function supportsVideoSequence(modelOrKey) {
  return Boolean(getVideoSequenceSupport(modelOrKey));
}

export function supportsVideoMultiPrompt(modelOrKey) {
  return getVideoSequenceSupport(modelOrKey) === VIDEO_SEQUENCE_SUPPORT.MULTI_PROMPT;
}

export function getVideoCreativeProfile(modelKey, mode = 't2v') {
  const model = getVideoModel(modelKey);
  if (!model) return null;
  const topology = mode === 'reference'
    ? ModelTopology.VIDEO_TO_VIDEO
    : mode === 'i2v'
      ? ModelTopology.IMAGE_TO_VIDEO
      : ModelTopology.TEXT_TO_VIDEO;
  const routeReferences = getRepositoryReferenceContract(modelKey, topology);
  const supportsPromptImages = Boolean(model.endpoints?.i2v)
    || routeReferences?.acceptedMediaTypes?.includes('image');
  const baseAdapter = supportsNativeElements(modelKey, topology)
    ? 'video-single-native-elements'
    : 'video-single-inline';
  const adapterKey = mode === 'sequence' ? 'video-sequence-inline' : baseAdapter;

  return {
    modelKey,
    family: 'video',
    requestMode: mode === 'reference' ? 'reference' : mode === 'i2v' ? 'i2v' : 't2v',
    adapterKey,
    supportsPromptImages,
    supportsNegativePrompt: false,
    supportsGuidanceScale: false,
    supportsInferenceSteps: false,
    supportsResolution: Array.isArray(model.capabilities?.resolutions) && model.capabilities.resolutions.length > 0,
    supportsAspectRatio: Array.isArray(model.capabilities?.aspectRatios) && model.capabilities.aspectRatios.length > 0,
    supportsAudio: Boolean(model.capabilities?.audio),
    supportsLastFrame: Boolean(model.capabilities?.lastFrame),
    supportsMultiPrompt: supportsVideoMultiPrompt(model),
    supportsSequence: supportsVideoSequence(model),
    supportsNativeElements: supportsNativeElements(modelKey, topology),
    promptBudget: 'medium',
    prefersConcisePrompt: model.key === 'klingV3' && supportsNativeElements(modelKey, topology),
    maxInputImages: routeReferences?.limits?.image
      ?? routeReferences?.limits?.total
      ?? (model.endpoints?.i2v ? 2 : 0),
    maxOutputImages: 1,
    claimedDimensionsFromImages: ['subject', 'style', 'scene', 'structure'],
  };
}
